// BOORO: the assets, the files a business uses and makes, such as logos,
// photos, templates and ad creatives. Folders on the side; the open folder
// shows its files as pictures, to open, move, replace or remove. They lie in
// the whole account or in one office, and agents keep their own there too.

import { folderName, inFolder } from "@booro/knowledge";
import { Trans, useLingui } from "@lingui/react/macro";
import type { KnowledgeFileSummary, KnowledgeFolder } from "@rakazo/contracts";
import { Button, Checkbox, Input } from "@rakazo/ui-web";
import { ChevronRight, Folder, Search, Trash2, Upload, X } from "lucide-react";
import type { DragEvent } from "react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { rpc } from "../../lib/rpc";
import { errorText } from "../../lib/user-error";
import { bytesOf, KnowledgeFileView } from "./knowledge-file";
import { fileIcon, KnowledgeTree, MoveMenu } from "./knowledge-tree";
import { base64Of } from "./knowledge-view";
import { useOrgSnapshot } from "./org-scope";
import type { WorkspaceChrome } from "./workspace-header";
import { WorkspaceViewHeader } from "./workspace-header";

type Place = { spaceId: string | null; path: string };

/** Pictures larger than this show their icon instead of a preview in the grid. */
const MAX_THUMB_BYTES = 6 * 1024 * 1024;

export function AssetsView({ title, chrome }: { title: string; chrome: WorkspaceChrome }) {
  const { t } = useLingui();
  const { snapshot } = useOrgSnapshot();
  const [files, setFiles] = useState<KnowledgeFileSummary[]>([]);
  const [folders, setFolders] = useState<KnowledgeFolder[]>([]);
  const [current, setCurrent] = useState<Place>({ spaceId: null, path: "" });
  const [fileId, setFileId] = useState<string | null>(null);
  const [query, setQuery] = useState("");
  const [chosen, setChosen] = useState<Set<string>>(new Set());
  const [uploading, setUploading] = useState(0);
  const [dropping, setDropping] = useState(false);
  const [error, setError] = useState("");
  const uploadInput = useRef<HTMLInputElement | null>(null);

  const reload = useCallback(async () => {
    try {
      const [nextFiles, nextFolders] = await Promise.all([
        rpc.booroKnowledge.files({ area: "assets" }),
        rpc.booroKnowledge.folders({ area: "assets" }),
      ]);
      setFiles(nextFiles);
      setFolders(nextFolders);
      setError("");
    } catch (cause) {
      setError(errorText(cause));
    }
  }, []);

  useEffect(() => {
    void reload();
  }, [reload]);

  const places = useMemo(
    () => [
      { id: null as string | null, name: t`Whole account` },
      ...(snapshot?.offices ?? []).map((office) => ({
        id: office.id as string | null,
        name: office.name,
      })),
    ],
    [snapshot, t],
  );
  const placeName = places.find((place) => place.id === current.spaceId)?.name ?? "";

  /** Runs a change, then shows the new state or why it failed. */
  async function change(run: () => Promise<unknown>) {
    try {
      await run();
      setError("");
    } catch (cause) {
      setError(errorText(cause));
    }
    await reload();
  }

  function open(place: Place) {
    setCurrent(place);
    setFileId(null);
    setChosen(new Set());
  }

  async function upload(place: Place, picked: File[]) {
    setUploading((count) => count + picked.length);
    const failed: string[] = [];
    for (const file of picked) {
      try {
        await rpc.booroKnowledge.uploadFile({
          spaceId: place.spaceId,
          folder: place.path,
          area: "assets",
          name: file.name,
          mimeType: file.type,
          contentBase64: await base64Of(file),
        });
      } catch (cause) {
        failed.push(`${file.name}: ${errorText(cause)}`);
      } finally {
        setUploading((count) => count - 1);
      }
    }
    setError(failed.join(" · "));
    await reload();
  }

  const needle = query.trim().toLowerCase();
  const shown = needle
    ? files.filter(
        (file) =>
          file.name.toLowerCase().includes(needle) || file.summary.toLowerCase().includes(needle),
      )
    : files.filter((file) => file.spaceId === current.spaceId && file.folder === current.path);
  const subfolders = needle
    ? []
    : folders
        .filter((folder) => folder.spaceId === current.spaceId)
        .map((folder) => folder.path)
        .filter(
          (path) =>
            path !== current.path &&
            inFolder(path, current.path) &&
            !path.slice(current.path ? current.path.length + 1 : 0).includes("/"),
        );
  const placeFolders = folders
    .filter((folder) => folder.spaceId === current.spaceId)
    .map((folder) => folder.path);
  const chosenFiles = files.filter((file) => chosen.has(file.id));

  function toggleChosen(id: string) {
    setChosen((before) => {
      const next = new Set(before);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  const dropHandlers = {
    onDragOver: (event: DragEvent) => {
      if (!event.dataTransfer.types.includes("Files")) return;
      event.preventDefault();
      setDropping(true);
    },
    onDragLeave: (event: DragEvent) => {
      if (!event.currentTarget.contains(event.relatedTarget as Node | null)) setDropping(false);
    },
    onDrop: (event: DragEvent) => {
      if (!event.dataTransfer.types.includes("Files")) return;
      event.preventDefault();
      setDropping(false);
      const dropped = [...event.dataTransfer.files];
      if (dropped.length > 0) void upload(current, dropped);
    },
  };

  const crumbs = current.path ? current.path.split("/") : [];

  return (
    <>
      <WorkspaceViewHeader
        title={title}
        chrome={chrome}
        actions={
          <Button
            size="sm"
            variant="outline"
            data-testid="assets-upload"
            onClick={() => uploadInput.current?.click()}
          >
            <Upload />
            <Trans>Upload</Trans>
          </Button>
        }
      />
      <input
        ref={uploadInput}
        type="file"
        multiple
        hidden
        data-testid="assets-upload-input"
        onChange={(event) => {
          const picked = [...(event.target.files ?? [])];
          event.target.value = "";
          if (picked.length > 0) void upload(current, picked);
        }}
      />
      {error ? (
        <p role="alert" className="px-4 py-2 text-sm text-destructive">
          {error}
        </p>
      ) : null}
      <div className="flex min-h-0 flex-1 flex-col md:flex-row">
        <aside className="flex w-full shrink-0 flex-col border-b border-border md:w-72 md:border-b-0 md:border-e">
          <div className="p-3">
            <div className="relative">
              <Search
                size={14}
                aria-hidden="true"
                className="pointer-events-none absolute start-2.5 top-1/2 -translate-y-1/2 text-muted-foreground"
              />
              <Input
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder={t`Search assets`}
                aria-label={t`Search assets`}
                className="ps-8"
              />
            </div>
          </div>
          <div className="rk-scroll flex-1 overflow-y-auto px-2 pb-3">
            <KnowledgeTree
              places={places}
              pages={[]}
              files={[]}
              folders={folders}
              query=""
              selected={null}
              reachIcon={() => null}
              onSelect={() => undefined}
              openFolder={needle ? null : current}
              onOpenFolder={(spaceId, path) => open({ spaceId, path })}
              onCreateFolder={(spaceId, path) =>
                change(() => rpc.booroKnowledge.createFolder({ spaceId, path, area: "assets" }))
              }
              onRenameFolder={(spaceId, from, to) =>
                change(async () => {
                  await rpc.booroKnowledge.renameFolder({ spaceId, from, to, area: "assets" });
                  if (current.spaceId === spaceId && inFolder(current.path, from)) {
                    open({ spaceId, path: `${to}${current.path.slice(from.length)}` });
                  }
                })
              }
              onRemoveFolder={(spaceId, path) =>
                void change(async () => {
                  await rpc.booroKnowledge.removeFolder({ spaceId, path, area: "assets" });
                  if (current.spaceId === spaceId && inFolder(current.path, path)) {
                    open({ spaceId, path: "" });
                  }
                })
              }
              onUpload={(spaceId, folder, picked) => void upload({ spaceId, path: folder }, picked)}
              onMove={(item, folder) =>
                void change(() => rpc.booroKnowledge.move({ kind: item.kind, id: item.id, folder }))
              }
            />
          </div>
          {uploading > 0 ? (
            <p
              role="status"
              className="border-t border-border px-4 py-2 text-[12.5px] text-muted-foreground"
            >
              <Trans>Uploading {uploading} files…</Trans>
            </p>
          ) : null}
        </aside>

        <section className="rk-scroll flex min-w-0 flex-1 flex-col overflow-y-auto">
          {fileId ? (
            <KnowledgeFileView
              key={fileId}
              fileId={fileId}
              snapshot={snapshot}
              folders={folders}
              onChanged={reload}
              onRemoved={() => {
                setFileId(null);
                void reload();
              }}
              onBack={() => setFileId(null)}
            />
          ) : (
            <div
              data-testid="assets-grid"
              {...dropHandlers}
              className={`flex flex-1 flex-col ${dropping ? "bg-accent/60 ring-2 ring-inset ring-ring" : ""}`}
            >
              <div className="flex min-h-12 flex-wrap items-center gap-1 border-b border-border px-4 py-2 text-[13px]">
                {needle ? (
                  <span className="text-muted-foreground">
                    <Trans>Search results</Trans>
                  </span>
                ) : (
                  <>
                    <button
                      type="button"
                      onClick={() => open({ spaceId: current.spaceId, path: "" })}
                      className="rounded px-1 hover:bg-accent"
                      dir="auto"
                    >
                      {placeName}
                    </button>
                    {crumbs.map((part, index) => {
                      const path = crumbs.slice(0, index + 1).join("/");
                      return (
                        <span key={path} className="flex items-center gap-1">
                          <ChevronRight
                            size={13}
                            aria-hidden="true"
                            className="text-muted-foreground rtl:-scale-x-100"
                          />
                          <button
                            type="button"
                            onClick={() => open({ spaceId: current.spaceId, path })}
                            className="rounded px-1 hover:bg-accent"
                            dir="auto"
                          >
                            {part}
                          </button>
                        </span>
                      );
                    })}
                  </>
                )}
                {chosenFiles.length > 0 ? (
                  <div
                    data-testid="assets-selection"
                    className="ms-auto flex items-center gap-1 text-muted-foreground"
                  >
                    <Trans>{chosenFiles.length} selected</Trans>
                    <MoveMenu
                      folders={placeFolders}
                      current={current.path}
                      onMove={(folder) =>
                        void change(async () => {
                          for (const file of chosenFiles) {
                            await rpc.booroKnowledge.move({ kind: "file", id: file.id, folder });
                          }
                          setChosen(new Set());
                        })
                      }
                    />
                    <Button
                      size="icon-sm"
                      variant="ghost"
                      aria-label={t`Delete selected files`}
                      onClick={() =>
                        void change(async () => {
                          for (const file of chosenFiles) {
                            await rpc.booroKnowledge.removeFile({ fileId: file.id });
                          }
                          setChosen(new Set());
                        })
                      }
                    >
                      <Trash2 />
                    </Button>
                    <Button
                      size="icon-sm"
                      variant="ghost"
                      aria-label={t`Clear selection`}
                      onClick={() => setChosen(new Set())}
                    >
                      <X />
                    </Button>
                  </div>
                ) : null}
              </div>

              {subfolders.length + shown.length === 0 ? (
                <div className="flex flex-1 items-center justify-center p-8">
                  <p className="text-sm text-muted-foreground">
                    {needle ? <Trans>Nothing found</Trans> : <Trans>Drop files here</Trans>}
                  </p>
                </div>
              ) : (
                <ul className="grid grid-cols-[repeat(auto-fill,minmax(9.5rem,1fr))] gap-3 p-4">
                  {subfolders.map((path) => (
                    <li key={path}>
                      <button
                        type="button"
                        data-testid={`assets-folder-${path}`}
                        onClick={() => open({ spaceId: current.spaceId, path })}
                        className="flex w-full flex-col items-center gap-2 rounded-xl border border-border p-3 hover:bg-accent"
                      >
                        <Folder
                          size={44}
                          strokeWidth={1.4}
                          aria-hidden="true"
                          className="text-muted-foreground"
                        />
                        <span className="w-full truncate text-[13px]" dir="auto">
                          {folderName(path)}
                        </span>
                      </button>
                    </li>
                  ))}
                  {shown.map((file) => (
                    <AssetTile
                      key={file.id}
                      file={file}
                      detail={needle ? file.folder || placeNameOf(places, file.spaceId) : ""}
                      chosen={chosen.has(file.id)}
                      onToggle={() => toggleChosen(file.id)}
                      onOpen={() => setFileId(file.id)}
                    />
                  ))}
                </ul>
              )}
            </div>
          )}
        </section>
      </div>
    </>
  );
}

function placeNameOf(places: { id: string | null; name: string }[], spaceId: string | null) {
  return places.find((place) => place.id === spaceId)?.name ?? "";
}

function AssetTile({
  file,
  detail,
  chosen,
  onToggle,
  onOpen,
}: {
  file: KnowledgeFileSummary;
  detail: string;
  chosen: boolean;
  onToggle: () => void;
  onOpen: () => void;
}) {
  const { t } = useLingui();
  return (
    <li
      className={`group relative rounded-xl border ${chosen ? "border-ring ring-1 ring-ring" : "border-border"}`}
    >
      <button
        type="button"
        data-testid={`assets-file-${file.id}`}
        onClick={onOpen}
        title={file.summary || file.name}
        className="flex w-full flex-col gap-2 rounded-xl p-2 text-start hover:bg-accent"
      >
        <AssetThumb file={file} />
        <span className="w-full truncate px-1 text-[13px]" dir="auto">
          {file.name}
        </span>
        {detail || file.summary ? (
          <span
            className="-mt-1.5 w-full truncate px-1 text-[11.5px] text-muted-foreground"
            dir="auto"
          >
            {detail || file.summary}
          </span>
        ) : null}
      </button>
      <Checkbox
        checked={chosen}
        onCheckedChange={onToggle}
        aria-label={t`Select ${file.name}`}
        className={`absolute start-3 top-3 bg-background ${
          chosen ? "" : "opacity-0 group-hover:opacity-100 focus-visible:opacity-100"
        }`}
      />
    </li>
  );
}

/** Object URLs of pictures already loaded, so the grid does not fetch them again. */
const thumbs = new Map<string, string>();

function AssetThumb({ file }: { file: KnowledgeFileSummary }) {
  const key = `${file.id}:${file.updatedAt}`;
  const picture = file.mimeType.startsWith("image/") && file.size <= MAX_THUMB_BYTES;
  const [url, setUrl] = useState(() => thumbs.get(key) ?? null);

  useEffect(() => {
    if (!picture || thumbs.has(key)) return;
    let active = true;
    void rpc.booroKnowledge
      .file({ fileId: file.id })
      .then((found) => {
        if (!found) return;
        // An <img> never runs what an SVG holds, so logos in SVG show too.
        const next = URL.createObjectURL(
          new Blob([bytesOf(found.contentBase64)], { type: found.file.mimeType }),
        );
        thumbs.set(key, next);
        if (active) setUrl(next);
      })
      .catch(() => undefined);
    return () => {
      active = false;
    };
  }, [file.id, key, picture]);

  const Icon = fileIcon(file.mimeType);
  return (
    <span className="grid aspect-square w-full place-items-center overflow-hidden rounded-lg bg-muted">
      {url ? (
        <img src={url} alt="" className="h-full w-full object-contain" />
      ) : (
        <Icon size={36} strokeWidth={1.4} aria-hidden="true" className="text-muted-foreground" />
      )}
    </span>
  );
}
